import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import SearchBox from "@/components/feature/SearchBox";
import MovieCard from "@/components/feature/MovieCard";
import { searchMovies, sortMovies, paginate } from "@/utils/movieUtils";
import { movies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const page = Number(params.get("page") || "1");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 350);
    return () => window.clearTimeout(timer);
  }, [keyword, page]);

  usePageMeta({
    title: keyword
      ? `搜索“${keyword}” - 岳母的诱惑`
      : "搜索影视 - 岳母的诱惑",
    description: keyword
      ? `岳母的诱惑搜索结果：与“${keyword}”相关的电影与电视剧剧情介绍与在线观看推荐。`
      : "在岳母的诱惑中搜索你感兴趣的电影与电视剧作品。",
    keywords: `岳母的诱惑,影视搜索,${keyword || "电影,电视剧"}`,
  });

  const results = useMemo(() => searchMovies(keyword), [keyword]);
  const { items, current, totalPages, total } = paginate(results, page, PAGE_SIZE);
  const hot = useMemo(() => sortMovies(movies, "heat").slice(0, 5), []);

  const changePage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Layout>
      <div className="bg-background-900/40 border-b border-background-800">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "搜索结果" },
            ]}
          />
          <h1 className="mt-5 font-heading text-2xl md:text-3xl font-black text-foreground-50">
            搜索结果
            {keyword && (
              <>
                ：
                <span className="text-accent-500">“{keyword}”</span>
              </>
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            {keyword
              ? `共找到 ${total} 部与关键词相关的影视作品`
              : "输入关键词，搜索你感兴趣的影视作品"}
          </p>
          <div className="mt-5 max-w-2xl">
            <SearchBox size="lg" defaultValue={keyword} />
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-12">
        {!keyword ? (
          <EmptyState
            icon="ri-search-line"
            title="请输入关键词开始搜索"
            description="支持按影片名称、类型、地区等关键词进行模糊匹配，例如“岳母”“悬疑”“韩国”。"
          />
        ) : loading ? (
          <div className="flex items-center justify-center py-24 gap-3 text-foreground-500">
            <span className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-background-800 border-t-primary-500 animate-spin">
              <i className="ri-loader-4-line text-lg"></i>
            </span>
            <span className="text-sm">正在搜索…</span>
          </div>
        ) : total === 0 ? (
          <div>
            <EmptyState
              icon="ri-emotion-sad-line"
              title={`未找到与“${keyword}”相关的影视`}
              description="换个关键词试试，或从下方热门推荐中挑选感兴趣的作品。"
              action={
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              }
            />
            <div className="mt-4">
              <h2 className="font-heading text-lg font-bold text-foreground-100 mb-5 text-center">
                热门影视推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {hot.map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <MovieGrid items={items} />
            <Pagination current={current} totalPages={totalPages} onChange={changePage} />
          </>
        )}
      </div>
    </Layout>
  );
}